import { configureStore } from "@reduxjs/toolkit";
import { persistStore, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { rootReducer, RETIRED_STORAGE_KEYS, RETIRED_STORAGE_PREFIXES } from "./rootReducer";
import { trackingMiddleware } from "./trackingMiddleware";

// Clear keys no build writes any more. `main.jsx` calls this once per load
// rather than leaving it to the logout purge, because a user who closes the tab
// never signs out and localStorage has no expiry.
export function dropRetiredStorageKeys() {
  RETIRED_STORAGE_KEYS.forEach((key) => {
    // redux-persist wraps every storage call in a Promise, so a failure
    // rejects here rather than throwing. Swallow it: the blob is inert, and
    // the next load tries again. A browser that blocks storage gets
    // redux-persist's noop adapter and never fails in the first place.
    storage.removeItem(key).catch(() => {});
  });
  // Prefixed families need the key list, which the redux-persist adapter does
  // not expose, so they are swept on localStorage directly.
  try {
    Object.keys(localStorage)
      .filter((key) => RETIRED_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix)))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Blocked storage holds no retired keys to clear.
  }
}

// Persistence is opt-in, one `persistReducer` per slice in `rootReducer.js`.
// There is no root persistReducer: wrapping the whole tree persisted every
// slice by default, so each exclusion had to be remembered and a forgotten one
// failed silently. A slice now reaches localStorage only by asking to.
//
// `persistStore` needs no root reducer to work. It tracks a registry that each
// nested `persistReducer` adds itself to on PERSIST, and flips `bootstrapped`
// once every entry has rehydrated, which is what opens `PersistGate`.
//
// That registry must not be empty. `bootstrapped` starts false and only flips
// while handling a REHYDRATE, so a `SLICE_PERSIST_CONFIGS` with no entries
// registers nothing, never rehydrates, and leaves `PersistGate loading={null}`
// rendering a blank page. Emptying that object means dropping the gate too.
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(trackingMiddleware),
});

// `manualPersist` holds the reads back until `storageOwner.js` knows whose
// state this browser holds. Started automatically, redux-persist would hand the
// previous person's slices to whoever signs in next, and nothing reacting
// afterwards can take them back.
export const persistor = persistStore(store, { manualPersist: true });
